import { v4 as uuidv4 } from 'uuid'

import { validateImageFiles } from '@common/media-layer/utils/image.utils'
import { validateVideoFiles } from '@common/media-layer/utils/video.utils'
import { validateDocumentFiles } from '@common/media-layer/utils/document-validation.utils'
import { isVoiceNoteAudioFile } from '@common/media-layer/utils/voice-note'

import type { ValidationError } from '../types/validation.types'
import type { DragDropValidation, DroppedFile } from '../types/drag-drop.types'

const detectFileType = (file: File): 'image' | 'video' | 'audio' | 'document' => {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  if (isVoiceNoteAudioFile(file)) return 'audio'
  return 'document'
}

/**
 * Same checks drag-and-drop uses: size, MIME, dimensions, duration.
 * Object URLs are created only for files that pass. Count limits stay in the editor.
 */
export const processDroppedFiles = async (
  files: File[],
  validation?: DragDropValidation
): Promise<{ droppedFiles: DroppedFile[]; errors: ValidationError[] }> => {
  const errors: ValidationError[] = []
  const validFiles: File[] = []

  const imageFiles = files.filter(file => detectFileType(file) === 'image')
  const videoFiles = files.filter(file => detectFileType(file) === 'video')
  const audioFiles = files.filter(file => detectFileType(file) === 'audio')
  const documentFiles = files.filter(file => detectFileType(file) === 'document')

  if (imageFiles.length > 0 && validation?.mediaValidations?.imageValidations) {
    const result = await validateImageFiles(imageFiles, validation.mediaValidations.imageValidations)
    validFiles.push(...result.valid)
    errors.push(...result.errors)
  } else if (imageFiles.length > 0) {
    validFiles.push(...imageFiles)
  }

  if (videoFiles.length > 0 && validation?.mediaValidations?.videoValidations) {
    const result = await validateVideoFiles(videoFiles, validation.mediaValidations.videoValidations)
    validFiles.push(...result.valid)
    errors.push(...result.errors)
  } else if (videoFiles.length > 0) {
    validFiles.push(...videoFiles)
  }

  if (audioFiles.length > 0 && validation?.audioValidations) {
    const result = await validateDocumentFiles(audioFiles, validation.audioValidations)
    validFiles.push(...result.valid)
    errors.push(...result.errors)
  } else if (audioFiles.length > 0) {
    validFiles.push(...audioFiles)
  }

  if (documentFiles.length > 0 && validation?.fileValidations) {
    const result = await validateDocumentFiles(documentFiles, validation.fileValidations)
    validFiles.push(...result.valid)
    errors.push(...result.errors)
  } else if (documentFiles.length > 0) {
    validFiles.push(...documentFiles)
  }

  const droppedFiles: DroppedFile[] = validFiles.map(file => ({
    _id: uuidv4(),
    file,
    url: URL.createObjectURL(file),
    name: file.name,
    size: file.size,
    type: file.type,
  }))

  return { droppedFiles, errors }
}
